import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { withAttribution } from "@/lib/outbound";
import { trackEvent } from "@/lib/tracking";
import LevelDot from "./LevelDot";
import {
  CATEGORIES,
  DUPR_REQUIREMENT_LABEL,
  SKILL_LEVELS,
  levelLabel,
} from "@/data/skillGuide";

interface ColorKeyProps {
  /** compact: one wrapping row of dot + label (homepage, open play). full: level, target and DUPR requirement. */
  variant?: "compact" | "full";
  onDark?: boolean;
  /**
   * Where each entry goes: "podplay" (default) opens the PodPlay schedule filtered to that
   * color's tag; "levels" jumps to the color on /levels; "none" renders plain text.
   */
  linkTo?: "podplay" | "levels" | "none";
  /** Show the "See the full color guide" link (compact only). */
  showGuideLink?: boolean;
  className?: string;
}

/** Color key for the schedule: seven skill colors, then pink and yellow categories. */
const ColorKey = ({
  variant = "compact",
  onDark = false,
  linkTo = "podplay",
  showGuideLink = true,
  className,
}: ColorKeyProps) => {
  const text = onDark ? "hsl(var(--velto-beige))" : "hsl(var(--velto-forest))";
  const entries = [
    ...SKILL_LEVELS.map((l) => ({
      color: l.color,
      slug: l.slug,
      label: levelLabel(l),
      gate: l.gate,
      url: l.eventsUrl,
    })),
    ...CATEGORIES.map((c) => ({
      color: c.color,
      slug: c.slug,
      label: c.name,
      gate: null as string | null,
      url: c.eventsUrl,
    })),
  ];
  const linkClass =
    "inline-flex items-center gap-2 rounded-sm underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

  const Entry = ({
    children,
    e,
  }: {
    children: React.ReactNode;
    e: (typeof entries)[number];
  }) => {
    if (linkTo === "podplay") {
      return (
        <a
          href={withAttribution(e.url)}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          style={{ color: text }}
          onClick={() =>
            trackEvent("book_court_click", {
              page:
                typeof window !== "undefined" ? window.location.pathname : "",
              location: `color_key_${e.slug}`,
              destination: e.url,
              link_text: e.label,
            })
          }
        >
          {children}
          <span className="sr-only">
            {" "}
            — find sessions on the schedule (opens in a new tab)
          </span>
        </a>
      );
    }
    if (linkTo === "levels") {
      return (
        <Link
          to={`/levels#${e.slug}`}
          className={linkClass}
          style={{ color: text }}
        >
          {children}
        </Link>
      );
    }
    return (
      <span className="inline-flex items-center gap-2" style={{ color: text }}>
        {children}
      </span>
    );
  };

  if (variant === "full") {
    return (
      <div className={className}>
        {linkTo === "podplay" && (
          <p
            className="mb-4 font-sweet text-sm opacity-80"
            style={{ color: text }}
          >
            Tap a color to see its sessions on the schedule.
          </p>
        )}
        <ul
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="Schedule color key"
        >
          {entries.map((e) => (
            <li key={e.slug} className="font-sweet">
              <Entry e={e}>
                <LevelDot color={e.color} onDark={onDark} />
                <span>
                  <span className="font-sweet-bold">{e.label}</span>
                  {e.gate && (
                    <span className="block text-sm opacity-80">
                      {DUPR_REQUIREMENT_LABEL}: {e.gate}
                    </span>
                  )}
                </span>
              </Entry>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={cn("font-sweet", className)}>
      <ul
        className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm"
        aria-label="Schedule color key"
      >
        {entries.map((e) => (
          <li key={e.slug}>
            <Entry e={e}>
              <LevelDot color={e.color} size="sm" onDark={onDark} />
              <span>{e.label}</span>
            </Entry>
          </li>
        ))}
      </ul>
      {linkTo === "podplay" && (
        <p
          className="mt-2 text-center text-xs opacity-80"
          style={{ color: text }}
        >
          Tap a color to see its sessions on the schedule.
        </p>
      )}
      {showGuideLink && (
        <p className="mt-3 text-center text-sm">
          <Link
            to="/levels"
            className="font-sweet-bold underline underline-offset-4"
            style={{ color: text }}
          >
            See the full color guide
          </Link>
        </p>
      )}
    </div>
  );
};

export default ColorKey;
